/**
 * Shared antd→Radix mapping helpers for the overlay seams.
 *
 * antd expresses position as one compound `placement` (`bottomLeft`); Radix
 * splits it into `side` + `align`. antd expresses open triggers as an array of
 * `hover|click|focus|contextMenu`; the seams need a set off that.
 *
 * Ported from the `update-design` reference branch (`src/antd/overlay/
 * placement.ts`), so a later merge finds the same helpers at the same path.
 * Leaf module: it imports nothing, because the seams that use it are reachable
 * from boot-static code (see the PRO-130 note in AGENTS.md).
 */

export type AntdPlacement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight'
  | 'left'
  | 'leftTop'
  | 'leftBottom'
  | 'right'
  | 'rightTop'
  | 'rightBottom'

export type RadixSide = 'top' | 'bottom' | 'left' | 'right'
export type RadixAlign = 'start' | 'center' | 'end'

const PLACEMENT_MAP: Record<
  AntdPlacement,
  { side: RadixSide; align: RadixAlign }
> = {
  top: { side: 'top', align: 'center' },
  topLeft: { side: 'top', align: 'start' },
  topRight: { side: 'top', align: 'end' },
  bottom: { side: 'bottom', align: 'center' },
  bottomLeft: { side: 'bottom', align: 'start' },
  bottomRight: { side: 'bottom', align: 'end' },
  left: { side: 'left', align: 'center' },
  leftTop: { side: 'left', align: 'start' },
  leftBottom: { side: 'left', align: 'end' },
  right: { side: 'right', align: 'center' },
  rightTop: { side: 'right', align: 'start' },
  rightBottom: { side: 'right', align: 'end' },
}

/**
 * Map an antd compound `placement` to Radix `{ side, align }`. Falls back to
 * the supplied default (antd's own defaults differ per component: Tooltip
 * defaults `top`, Popover/Dropdown `bottomLeft`).
 */
export function toRadixSideAlign(
  placement: AntdPlacement | undefined,
  fallback: AntdPlacement
): { side: RadixSide; align: RadixAlign } {
  return PLACEMENT_MAP[placement ?? fallback] ?? PLACEMENT_MAP[fallback]
}

/**
 * Drop styled-components transient (`$`-prefixed) props so they never reach
 * the DOM. Both overlay seams are wrapped by `styled(Seam)` at live call sites
 * (the house Modal, several local styled tooltips), and `styled()` on a
 * COMPONENT forwards `$foo` straight through; without this they would spread
 * onto a DOM node and warn.
 */
export function omitTransient<T extends Record<string, unknown>>(
  props: T
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(props).filter(([key]) => !key.startsWith('$'))
  )
}

export type AntdTrigger = 'hover' | 'click' | 'focus' | 'contextMenu'

/**
 * Normalise antd's `trigger` (string | string[] | undefined) to a Set.
 *
 * An EMPTY array is not the same as `undefined` and must not fall back: this
 * repo uses `trigger={cond ? ['hover'] : []}` to switch a tooltip off
 * (`FormInviteUser`), so collapsing `[]` onto the default would show a bubble
 * that is meant to stay hidden.
 */
export function normaliseTriggers(
  trigger: AntdTrigger | AntdTrigger[] | undefined,
  fallback: AntdTrigger
): Set<AntdTrigger> {
  if (trigger == null) return new Set([fallback])
  return new Set(Array.isArray(trigger) ? trigger : [trigger])
}
